import type {
  WordPressPublicationItem,
  WordPressPublicationType,
  WordPressSyncWarning,
  WordPressSyncWarningTargetType,
} from './types';

export const WORDPRESS_SYNC_WARNING_AUTO_CLEAR_TEXT = 'Varningen försvinner automatiskt vid nästa synk.';

const TARGET_TYPE_LABELS: Record<WordPressSyncWarningTargetType, string> = {
  taxonomy: 'Kategori',
  winery: 'Producent',
  acf_choice: 'Urvalsfält',
  relationship: 'Kopplat innehåll',
  config: 'Inställning',
};

const TARGET_TYPE_INSTRUCTIONS: Record<WordPressSyncWarningTargetType, string> = {
  taxonomy: 'Skapa kategorin i WordPress eller ändra värdet här.',
  winery: 'Skapa producenten i WordPress eller ändra värdet här.',
  acf_choice: 'Lägg till valet i WordPress eller ändra värdet här.',
  relationship: 'Synka eller publicera det kopplade innehållet först.',
  config: 'Kontakta WordPress-administratören.',
};

const PUBLICATION_TARGET_LABELS: Record<WordPressPublicationType, string> = {
  dvh: 'DVH',
  foodservice: 'Food Service',
  wine: 'Vin',
  recipe: 'Recept',
};

export const WORDPRESS_SYNC_WARNING_TARGET_TYPE_OPTIONS = (
  Object.keys(TARGET_TYPE_LABELS) as WordPressSyncWarningTargetType[]
).map(value => ({ value, label: TARGET_TYPE_LABELS[value] }));

export function getWordPressSyncWarningTargetTypeLabel(type: WordPressSyncWarningTargetType | '' | null | undefined): string {
  return type ? TARGET_TYPE_LABELS[type] || 'Övrigt' : 'Övrigt';
}

export function getWordPressPublicationTargetLabel(target: WordPressPublicationType): string {
  return PUBLICATION_TARGET_LABELS[target] || target;
}

export function getSafeWordPressSyncWarningUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

function withFieldPrefix(fieldLabel: string, text: string): string {
  if (fieldLabel) return `${fieldLabel}: ${text}`;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function getWordPressTargetName(warning: Pick<WordPressSyncWarning, 'wordpressTargetLabel' | 'wordpressTarget'>): string {
  return warning.wordpressTargetLabel || warning.wordpressTarget || '';
}

export function getWordPressSyncWarningInstruction(
  warning: Pick<WordPressSyncWarning, 'code' | 'fieldLabel' | 'values' | 'resolvedTargetType' | 'wordpressTargetLabel' | 'wordpressTarget' | 'message'>,
): string {
  const fieldLabel = warning.fieldLabel || '';
  const wordpressName = getWordPressTargetName(warning);
  const plural = (warning.values?.length || 0) > 1;

  let text: string;
  switch (warning.code) {
    case 'term_not_found':
      text = withFieldPrefix(
        fieldLabel,
        `${plural ? 'värdena finns' : 'värdet finns'} inte i WordPress${wordpressName ? ` (${wordpressName})` : ''} och kopplades inte. Skapa kategorin i WordPress eller ändra värdet här.`,
      );
      break;
    case 'choice_not_found':
      text = withFieldPrefix(
        fieldLabel,
        `${plural ? 'värdena finns' : 'värdet finns'} inte som val i WordPress. Lägg till valet${wordpressName ? ` i fältet ${wordpressName}` : ''} i WordPress eller ändra värdet här.`,
      );
      break;
    case 'winery_not_found':
      text = withFieldPrefix(
        fieldLabel,
        'producenten finns inte i WordPress och kopplades inte. Skapa producenten i WordPress eller ändra värdet här.',
      );
      break;
    case 'relationship_not_found':
      text = withFieldPrefix(
        fieldLabel,
        'det kopplade innehållet hittades inte i WordPress. Synka eller publicera det kopplade innehållet först.',
      );
      break;
    case 'config_error':
      text = `Inställningsfel i WordPress-pluginet${wordpressName ? ` (${wordpressName})` : ''}. Kontakta WordPress-administratören.`;
      break;
    default: {
      const message = (warning.message || '').trim() || 'WordPress rapporterade en varning.';
      const instruction = warning.resolvedTargetType ? TARGET_TYPE_INSTRUCTIONS[warning.resolvedTargetType] : '';
      const base = fieldLabel ? `${fieldLabel}: ${message}` : message;
      text = instruction ? `${base.replace(/[.\s]*$/, '')}. ${instruction}` : base;
      break;
    }
  }

  return `${text} ${WORDPRESS_SYNC_WARNING_AUTO_CLEAR_TEXT}`;
}

export function getWordPressSyncWarningCount(publication: WordPressPublicationItem['publication'] | null | undefined): number {
  if (!publication) return 0;
  if (typeof publication.syncWarningCount === 'number') return publication.syncWarningCount;
  return Array.isArray(publication.syncWarnings) ? publication.syncWarnings.length : 0;
}

export function formatWordPressSyncWarningCount(count: number): string {
  return count === 1 ? '1 synkvarning' : `${count} synkvarningar`;
}

export function countWordPressItemsWithSyncWarnings(items: WordPressPublicationItem[]): number {
  return items.filter(item => getWordPressSyncWarningCount(item.publication) > 0).length;
}

export function formatWordPressSyncWarningTime(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' });
}
